import {
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  retryLink,
  splitLink,
} from "@trpc/client";
import { EventSourcePolyfill } from "event-source-polyfill";
import {
  createConversationTransport,
  createSubscriptionAuthRecovery,
} from "@chatable/chat-client/trpc";
import type { AppRouter } from "@chatable/api/router";
import {
  conversationListResponseSchema,
  type ConversationListInput,
} from "@chatable/schemas/conversations/conversation";
import { authenticatedFetch } from "../api/trpc";
import { getAuthToken, getRefreshFunction } from "../api/axios";

const subscriptionAuth = createSubscriptionAuthRecovery(() => getRefreshFunction()?.());

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function createClient() {
  const url = `${import.meta.env.VITE_API_BASE_URL}/trpc`;
  return createTRPCClient<AppRouter>({
    links: [
      splitLink({
        condition: operation => operation.type === "subscription",
        true: [
          retryLink({
            retry: options =>
              options.attempts < 2 &&
              (options.error.data?.code === "UNAUTHORIZED" ||
                options.error.data?.code === "FORBIDDEN") &&
              subscriptionAuth.recover(),
          }),
          httpSubscriptionLink({
            url,
            EventSource: EventSourcePolyfill,
            eventSourceOptions: async () => {
              await subscriptionAuth.wait();
              return { headers: authHeaders(), withCredentials: true };
            },
          }),
        ],
        false: httpBatchLink({
          url,
          fetch: authenticatedFetch,
          methodOverride: "POST",
        }),
      }),
    ],
  });
}

const client = createClient();
const conversationTransport = createConversationTransport(client);

export function createWebConversationTransport() {
  return conversationTransport;
}

export async function listWebConversations(input: ConversationListInput) {
  return conversationListResponseSchema.parse(await client.conversations.list.query(input));
}

export async function exchangeWebVoiceSdp(input: {
  conversationId: string;
  sdpOffer: string;
  mode: "book" | "audiobook" | "companion" | "search";
  bookId?: string;
  timeS?: number;
  versionId?: string | null;
}) {
  const result = await client.voice.exchangeSDP.mutate({
    sdp_offer: input.sdpOffer,
    conversationId: input.conversationId,
    mode: input.mode,
    bookId: input.bookId,
    timeS: input.timeS,
    versionId: input.versionId,
  });
  if (!("sdp_answer" in result)) throw new Error("Voice session setup failed");
  return {
    sdpAnswer: result.sdp_answer,
    callId: result.call_id,
    conversationId: result.conversation_id,
    iceServers: result.ice_servers,
  };
}
